/**
 * 나의 정보 (/my/profile/ · page-partner-profile.php).
 *
 * 담당자 이름 · 휴대전화 · 이메일을 admin-ajax(Partner_Profile)로 보내 매장 사이트에 저장한다.
 * 이메일은 로그인 인증 코드가 가는 곳이라, 처음 값과 달라지면 [인증코드 요청]이 열리고
 * 받은 코드를 함께 보내야 저장된다(확인은 매장 사이트가 한다). 저장이 끝나면 새 값으로 다시 그리게 새로고침한다.
 *
 * 매장 삭제 요청은 확인 모달에서 아이디를 그대로 입력해야 버튼이 켜진다. 접수되면 매장 사이트가 매장을
 * 비공개로 내리므로, 새로고침해 버튼 대신 요청 안내를 그리게 한다.
 *
 * 요청 뒤에는 매장 사이트가 대기 기간 동안 삭제를 거절한다(삭제 잠금). [받을 자료가 없습니다] 는 그 기간을
 * 원장 본인이 앞당기는 확인이고, 같은 모양의 모달(아이디 입력)을 쓴다 — initConfirmDialog.
 *
 * 추천 코드(담당자 정보 카드 맨 아래)는 관리자 세션에만 입력칸 + [저장]이 있고, 담당자 정보 [저장]과 따로
 * qrshop_profile_referral 로 보낸다 — initReferralField. 읽기 전용 세션(입력칸 없음)은 아무것도 잇지 않는다.
 */

import { busy } from './busyButton';
import initCodeInput from './codeInput';
import { lockModal } from './initModal';
import { postForm, reloadWith as reloadWithKey, setNotice, showFlash } from './partnerForm';
import initScrollSpyNav from './scrollSpyNav';

/** 새로고침한 화면에 띄울 알림을 맡겨 두는 자리 */
const FLASH_KEY = 'qrshop_profile_flash';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^01[016789]\d{7,8}$/;

/** 추천 코드 모양 — 서버(Referral::PATTERN · qrshop-api) 와 같다. 빈 값은 "떼기" 라 따로 본다 */
const REFERRAL_PATTERN = /^[A-Z0-9-]{3,16}$/;

const InitPartnerProfile = () => {
  const form = document.querySelector('[data-profile-form]');

  if (!form) {
    return;
  }

  showFlash(FLASH_KEY);

  // 좌측 섹션 메뉴 (모바일: 상단 칩) — /register/ 와 같은 방식
  const navEl = document.querySelector('[data-profile-nav]');
  if (navEl) {
    initScrollSpyNav(
      navEl,
      Array.from(form.querySelectorAll('[data-nav-label]')).map((section) => ({
        el: section,
        label: section.dataset.navLabel,
      }))
    );
  }

  const status = form.querySelector('[data-profile-status]');
  const saveButton = form.querySelector('[data-profile-save]');
  const sendButton = form.querySelector('[data-profile-email-send]');
  const resendButton = form.querySelector('[data-profile-email-resend]');
  const emailHint = form.querySelector('[data-profile-email-hint]');
  const codeStep = form.querySelector('[data-profile-code-step]');
  const code = initCodeInput(form.querySelector('[data-profile-code]'));
  const field = (name) => form.elements.namedItem(name);
  const value = (name) => field(name)?.value.trim() || '';
  const originalEmail = (form.dataset.email || '').toLowerCase();

  const setStatus = (message, state = 'error') => setNotice(status, message, state);

  // 인증 코드를 보낸 주소 — 이 주소에서 또 바뀌면 코드를 다시 받아야 한다
  let codeSentTo = '';

  const emailChanged = () => value('email').toLowerCase() !== originalEmail;

  const syncEmail = () => {
    const changed = emailChanged();
    const email = value('email').toLowerCase();

    sendButton.disabled = !changed || !EMAIL_PATTERN.test(email);
    emailHint.hidden = changed;

    // 원래 주소로 되돌리거나 코드를 받은 뒤 주소를 또 고치면 코드 칸을 닫는다
    if (!changed || (codeSentTo && codeSentTo !== email)) {
      codeStep.hidden = true;
      code.clear();
      codeSentTo = '';
    }
  };

  field('email').addEventListener('input', syncEmail);
  syncEmail();

  // 고치기 시작하면 지난번 안내는 걷어 낸다 (체크박스도 input 이벤트를 낸다)
  form.addEventListener('input', () => setStatus(''));

  // 숫자만 남긴다 — 복사해 붙이면 하이픈 · 공백이 섞이는 일이 잦다
  field('phone').addEventListener('input', () => {
    field('phone').value = field('phone').value.replace(/\D/g, '').slice(0, 11);
  });

  const requestCode = async () => {
    // sendButton · resendButton 이 같은 요청을 쓴다 — 진행 중 더블클릭이면 무시
    if ('true' === sendButton.getAttribute('aria-busy')) {
      return;
    }

    const email = value('email').toLowerCase();

    if (!EMAIL_PATTERN.test(email)) {
      setStatus('이메일 주소를 확인해 주세요.');
      field('email').focus();
      return;
    }

    const release = busy(sendButton, '보내는 중…');
    // 원은 sendButton 에만, resendButton(다시 요청 링크)은 진행 중 잠그기만 한다
    if (resendButton) {
      resendButton.disabled = true;
    }
    setStatus('인증 코드를 보내는 중입니다...', 'warning');

    try {
      const data = await postForm(form, 'qrshop_profile_email_code', { email });

      codeSentTo = email;
      codeStep.hidden = false;
      code.clear();
      code.focus();
      setStatus(data.message || '입력하신 이메일로 인증 코드를 보냈습니다.', 'success');
    } catch (error) {
      setStatus(error.message);
    } finally {
      release();
      if (resendButton) {
        resendButton.disabled = false;
      }
      syncEmail();
    }
  };

  sendButton.addEventListener('click', requestCode);
  resendButton?.addEventListener('click', requestCode);

  // ── 추천 코드 (관리자 세션만 입력칸이 있다) ──────────────────────────────
  initReferralField(form);

  // ── 수신 · 활용 동의 ────────────────────────────────────────────────────
  // 카드는 매장 사이트가 consents 를 줄 때만(qrshop-api ≥ 1.36.0, 관리자 세션 제외) 그려진다.
  // 별도 저장 버튼은 없다 — 아래 제출 핸들러가 이 컨트롤러를 통해 먼저 동의를 보내고, 이어서 담당자 정보를 저장한다.
  const consent = initConsentCard(form, form.querySelector('[data-profile-consent]'));

  // 동의 저장이 성공한 뒤 담당자 정보 저장이 실패해 다시 누르는 경우, 동의는 다시 보내지 않으므로(needsSave() 가
  // false) 이 값들을 핸들러 밖에 둬 재시도에도 남겨 둔다 — 알림 문구와 "동의는 이미 저장됨" 안내에 쓴다
  let consentNotified = false;
  let consentSaved = false;

  /** 비었거나 형식이 틀린 첫 칸 — 없으면 null */
  const findProblem = () => {
    if ('' === value('nickname')) {
      return { message: '담당자 이름을 입력해 주세요.', focus: field('nickname') };
    }

    if (!EMAIL_PATTERN.test(value('email'))) {
      return { message: '이메일 주소를 확인해 주세요.', focus: field('email') };
    }

    if (emailChanged() && !codeSentTo) {
      return { message: '바꾼 이메일로 [인증코드 요청]을 먼저 눌러 주세요.', focus: sendButton };
    }

    if (emailChanged() && 6 !== code.value().length) {
      return { message: '메일로 받은 6자리 인증 코드를 입력해 주세요.', focus: code };
    }

    if (!PHONE_PATTERN.test(value('phone'))) {
      return {
        message: '휴대전화 번호를 01011112222 형식(숫자만)으로 입력해 주세요.',
        focus: field('phone'),
      };
    }

    return null;
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const problem = findProblem();

    if (problem) {
      setStatus(problem.message);
      problem.focus?.focus();
      return;
    }

    // 필수 동의가 남아 있으면 여기서 막는다 — 아무것도 보내지 않는다(/register/ 와 같은 방식,
    // 꺼진 버튼은 왜 안 되는지 알려 주지 못한다)
    if (consent) {
      const missing = consent.missingRequired();

      if (missing.length > 0) {
        const labels = missing.map((input) => input.dataset.profileConsentLabel || '필수 항목');
        const message = `[필수] ${labels.join(', ')} 항목을 체크해 주세요. 필수 항목에 동의해야 저장됩니다.`;

        setStatus(message);
        // 스크롤해서 보이는 곳이 카드 안이라, 맨 아래 상태와 같은 경고를 카드 위 안내에도 띄운다
        consent.showMissingWarning(message);
        // 모바일에서 focus() 만으로는 칸까지 스크롤되지 않을 수 있어 먼저 가운데로 옮긴다
        missing[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
        missing[0].focus({ preventScroll: true });
        return;
      }
    }

    const release = busy(saveButton, '저장 중…');
    setStatus('');

    // 동의 카드에 바뀐 게 있으면(필수 동의를 새로 채웠거나 선택 체크를 바꿨으면) 담당자 정보보다 먼저 보낸다.
    // 실패하면 담당자 정보는 아예 보내지 않는다 — 카드 안 체크는 이미 실제 기록대로 맞춰졌으니 다시 누르면
    // 바뀌지 않은 동의는 다시 보내지 않는다
    if (consent && consent.needsSave()) {
      try {
        consentNotified = (await consent.save()).notified;
        consentSaved = true;
      } catch (error) {
        setStatus(error.message);
        release();
        return;
      }
    }

    try {
      const data = await postForm(form, 'qrshop_profile_save', {
        nickname: value('nickname'),
        email: value('email').toLowerCase(),
        phone: value('phone'),
        code: emailChanged() ? code.value() : '',
      });
      const notifiedText = consentNotified ? ' 동의 처리 결과를 이메일로 보내 드렸습니다.' : '';

      // 헤더 "○○ 님" 과 1:1 문의 모달 기본값까지 새 값으로 그리게 새로고침한다
      reloadWithKey(FLASH_KEY, (data.message || '나의 정보를 저장했습니다.') + notifiedText);
    } catch (error) {
      // 동의는 이미 저장됐는데(consentSaved) 담당자 정보만 실패했으면, 다시 눌러도 동의는 또 보내지 않는다고 알려 준다
      const consentText = consentSaved
        ? ' 동의 내용은 저장했습니다. 담당자 정보를 확인해 다시 [저장]을 눌러 주세요.'
        : '';

      setStatus(error.message + consentText);
      release();
    }
  });

  // ── 매장 삭제 요청 · 받을 자료 없음 ─────────────────────────────────────
  // 두 모달은 같은 틀이다: 아이디를 그대로 입력해야 버튼이 켜지고, 끝나면 서버가 화면을 다시 그린다.
  // 서버가 조건에 맞는 것만 그린다 — 삭제 요청: 주인 본인 세션 + 새 플러그인(≥ 1.32.0) + 아직 요청 전,
  // 받을 자료 없음: 요청 뒤 + 삭제 잠금이 있는 플러그인(≥ 1.34.0) + 아직 잠겨 있을 때. 둘이 함께 있는 일은 없다.
  initConfirmDialog(document.querySelector('[data-profile-delete]'), {
    action: 'qrshop_profile_delete_request',
    busyLabel: '요청하는 중…',
    done: '매장 삭제 요청이 접수되었습니다.',
  });

  initConfirmDialog(document.querySelector('[data-profile-waive]'), {
    action: 'qrshop_profile_delete_waive',
    busyLabel: '확인하는 중…',
    done: '확인했습니다. 매장은 대기 기간 없이 삭제될 수 있습니다.',
  });
};

/**
 * 추천 코드 칸(담당자 정보 카드 맨 아래) — 관리자 세션에만 있는 입력칸 + [저장]을 잇는다.
 *
 * 담당자 정보 폼 안에 있지만 그 폼의 제출과는 별개다: 입력칸에 name 이 없어 폼 값에 섞이지 않고, Enter 는 여기서 막아
 * 담당자 정보 [저장]이 아니라 이 저장을 부른다. 값은 /register/ 와 같은 모양으로 다듬고(대문자 · 공백 제거), 빈 값은 "떼기" 다.
 * 서버(qrshop_profile_referral)가 관리자 세션 · 코드 존재 · 대상 매장을 다시 확인하고, 응답의 referral · hint 로 칸을 새로 그린다.
 * 새로고침하지 않는다 — 이 칸만 바뀌고 다른 칸의 입력 중인 값이 날아가면 안 된다.
 *
 * @param {HTMLFormElement} form data-ajax-url · data-nonce 를 가진 폼
 */
const initReferralField = (form) => {
  const wrap = form.querySelector('[data-profile-referral]');
  const input = wrap?.querySelector('[data-profile-referral-input]');
  const button = wrap?.querySelector('[data-profile-referral-save]');

  // 읽기 전용 세션(입력칸 없음) · 옛 플러그인(칸 없음)
  if (!wrap || !input || !button) {
    return;
  }

  const hint = wrap.querySelector('[data-profile-referral-hint]');
  const status = wrap.querySelector('[data-profile-referral-status]');
  const normalize = (value) =>
    String(value || '')
      .toUpperCase()
      .replace(/\s+/g, '');

  /** 마지막으로 저장된 값 — 같으면 보내지 않는다(서버도 kept 로 답하지만 왕복을 아낀다) */
  let saved = normalize(input.value);

  input.addEventListener('input', () => {
    const normalized = normalize(input.value);

    if (input.value !== normalized) {
      input.value = normalized;
    }

    setNotice(status, '');
  });

  const save = async () => {
    if ('true' === button.getAttribute('aria-busy')) {
      return;
    }

    const code = normalize(input.value);

    if ('' !== code && !REFERRAL_PATTERN.test(code)) {
      setNotice(status, '추천 코드는 영문 대문자 · 숫자 · 하이픈 3~16자입니다.');
      input.focus();
      return;
    }

    if (code === saved) {
      setNotice(status, '바뀐 내용이 없습니다.', 'warning');
      return;
    }

    const release = busy(button, '저장 중…');
    setNotice(status, '');

    try {
      const data = await postForm(form, 'qrshop_profile_referral', { code });
      const referral = data.referral || {};

      // 매장 사이트가 확정한 값으로 칸을 맞춘다(요청과 다르게 저장됐어도 실제 값을 보여 준다)
      input.value = 'string' === typeof referral.code ? referral.code : code;
      saved = normalize(input.value);

      if (hint && 'string' === typeof data.hint) {
        hint.textContent = data.hint;
      }

      setNotice(status, data.message || '추천 코드를 저장했습니다.', 'success');
    } catch (error) {
      setNotice(status, error.message);
    } finally {
      release();
    }
  };

  button.addEventListener('click', save);

  // Enter 는 담당자 정보 폼의 암묵적 제출이 아니라 이 저장이다
  input.addEventListener('keydown', (event) => {
    if ('Enter' === event.key && !event.isComposing) {
      event.preventDefault();
      save();
    }
  });
};

/**
 * 수신 · 활용 동의 카드 — 소식 받기 매체 셋 · 매장 소개 활용 체크와, 기록에 없는 필수 동의 체크를 관리한다.
 *
 * 별도 저장 버튼은 없다 — 이 카드는 아래 메인 [저장]이 부르는 컨트롤러다. 반환한 missingRequired() 로 빠진
 * 필수 체크를 먼저 확인하고(있으면 메인 핸들러가 막고, showMissingWarning() 으로 카드 위 안내에도 같은 경고를 띄운다),
 * needsSave() 로 보낼 게 있는지(필수 동의가 남아 있거나 선택 체크가 마지막 저장값과 달라졌을 때) 본 뒤 save() 로
 * qrshop_profile_consent 를 보낸다. 저장 결과의 consents 로 체크와 "처음 값" 을 맞추고, 기록에 남은 필수 동의는
 * 체크 칸을 걷어 내 기록 목록에 한 줄로 옮긴다. 카드가 없으면(매장 사이트가 consents 를 안 주거나 관리자 세션) null 을 돌려준다.
 *
 * @param {HTMLFormElement} form data-ajax-url · data-nonce 를 가진 폼
 * @param {Element | null} card [data-profile-consent] 카드
 * @returns {{
 *   missingRequired: () => HTMLInputElement[],
 *   needsSave: () => boolean,
 *   save: () => Promise<{ notified: boolean }>,
 *   showMissingWarning: (message: string) => void,
 * } | null}
 */
const initConsentCard = (form, card) => {
  if (!card) {
    return null;
  }

  const inputs = Array.from(card.querySelectorAll('[data-profile-consent-input]'));
  let required = Array.from(card.querySelectorAll('[data-profile-consent-required]'));
  const notice = card.querySelector('[data-profile-consent-notice]');
  const noticeText = notice?.querySelector('.notice__text');
  const recorded = card.querySelector('[data-profile-consent-recorded]');
  const record = card.querySelector('[data-profile-consent-record]');

  if (0 === inputs.length && 0 === required.length) {
    return null;
  }

  /** 마지막으로 저장된 값 — 이것과 다를 때만 보낼 게 있다고 본다 */
  let saved = new Map(inputs.map((input) => [input.dataset.profileConsentInput, input.checked]));

  const all = () => inputs.concat(required);

  const dirty = () =>
    inputs.some((input) => saved.get(input.dataset.profileConsentInput) !== input.checked);

  const missingRequired = () => required.filter((input) => !input.checked);

  /** 보낼 게 있을까 — 필수 체크가 남아 있으면 늘(새로 채웠는지 확인해야 한다), 없으면 선택 체크가 바뀌었을 때만 */
  const needsSave = () => required.length > 0 || dirty();

  /**
   * 필수 체크가 빠져 제출이 막혔을 때, 스크롤해서 보이는 카드 위 안내에도 같은 경고를 띄운다
   * (맨 아래 상태 표시는 화면 밖일 수 있다).
   *
   * @param {string} message
   */
  const showMissingWarning = (message) => {
    if (!notice) {
      return;
    }

    if (noticeText) {
      noticeText.textContent = message;
    }
    notice.hidden = false;
  };

  /**
   * 저장으로 기록에 남은 필수 동의 — 체크 칸을 걷어 내고 기록 목록에 한 줄로 그린다.
   *
   * @param {Record<string, string>} texts 서버가 만든 기록 글(required_text — "2026년 9월 23일 10:00 · … 시행 약관")
   * @param {Record<string, { agreed?: boolean }>} consents 서버가 확정한 기록
   */
  const settleRequired = (texts, consents) => {
    required = required.filter((input) => {
      const key = input.dataset.profileConsentRequired;

      if (true !== consents[key]?.agreed) {
        return true;
      }

      if (record) {
        const row = document.createElement('div');
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');

        row.className = 'consent-record__row';
        dt.textContent = input.dataset.profileConsentLabel || key;
        dd.textContent = texts[key] || '동의함';
        row.append(dt, dd);
        record.append(row);
      }

      card.querySelector(`[data-profile-consent-required-field="${key}"]`)?.remove();

      return false;
    });

    if (recorded && record && record.children.length > 0) {
      recorded.hidden = false;
    }

    if (notice && 0 === required.length) {
      notice.hidden = true;
    }
  };

  /**
   * qrshop_profile_consent 를 보낸다 — missingRequired() 가 비어 있을 때만 메인 핸들러가 부른다.
   * 저장하는 동안 카드의 모든 체크를 잠근다. 실패하면 에러를 그대로 던진다(메인 핸들러가 잡아 보여 준다).
   *
   * @returns {Promise<{ notified: boolean }>}
   */
  const save = async () => {
    const fields = {};
    inputs.forEach((input) => {
      fields[input.dataset.profileConsentInput] = input.checked ? '1' : '0';
    });
    // 필수 동의는 켜진 것만 보낸다 — '0' 은 철회가 아니라 "보내지 않음" 이다(서버도 그렇게 본다)
    required.forEach((input) => {
      if (input.checked) {
        fields[`${input.dataset.profileConsentRequired}_consent`] = '1';
      }
    });

    all().forEach((input) => {
      input.disabled = true;
    });

    try {
      const data = await postForm(form, 'qrshop_profile_consent', fields);
      const consents = data.consents || {};

      // 매장 사이트가 확정한 값으로 체크와 "처음 값" 을 맞춘다(요청과 다르게 저장됐어도 화면이 실제 기록을 보여 준다)
      inputs.forEach((input) => {
        const key = input.dataset.profileConsentInput;
        const entry =
          'promo' === key ? consents.promo : consents.marketing?.[key.replace('marketing_', '')];

        if (entry && 'boolean' === typeof entry.agreed) {
          input.checked = entry.agreed;
        }
      });
      saved = new Map(inputs.map((input) => [input.dataset.profileConsentInput, input.checked]));
      settleRequired(data.required_text || {}, consents);

      return { notified: Boolean(data.notified) };
    } finally {
      all().forEach((input) => {
        input.disabled = false;
      });
    }
  };

  return { missingRequired, needsSave, save, showMissingWarning };
};

/**
 * 아이디 입력 확인 모달 하나를 잇는다.
 *
 * @param {Element | null} dialog data-ajax-url · data-nonce · data-login 을 가진 <dialog>
 * @param {{ action: string, busyLabel: string, done: string }} options admin-ajax 액션 · 처리 중 버튼 글 · 서버 문구가 없을 때의 알림
 */
const initConfirmDialog = (dialog, { action, busyLabel, done }) => {
  if (!(dialog instanceof HTMLDialogElement)) {
    return;
  }

  const input = dialog.querySelector('[data-profile-confirm-input]');
  const button = dialog.querySelector('[data-profile-confirm-submit]');
  const status = dialog.querySelector('[data-profile-confirm-status]');
  const login = (dialog.dataset.login || '').trim();

  if (!input || !button) {
    return;
  }

  const sync = () => {
    // 아이디를 글자 그대로 입력했을 때만 켠다 (서버도 세션 아이디 · user_login 과 다시 대조한다)
    button.disabled = '' === login || input.value.trim() !== login;
  };

  input.addEventListener('input', () => {
    setNotice(status, '');
    sync();
  });

  // 닫았다 다시 열면 처음부터 — 입력해 둔 아이디가 남아 있으면 다음에 실수로 누르기 쉽다
  dialog.addEventListener('close', () => {
    input.value = '';
    setNotice(status, '');
    sync();
  });

  sync();

  button.addEventListener('click', async () => {
    if ('' === login || input.value.trim() !== login) {
      setNotice(status, '매장명(아이디)을 정확히 입력해 주세요.');
      input.focus();
      return;
    }

    const unlock = lockModal(dialog, button, busyLabel);

    try {
      const data = await postForm(dialog, action, { confirm: input.value.trim() });

      // 버튼 대신 바뀐 안내를 그리도록 서버가 화면을 다시 그리게 한다
      reloadWithKey(FLASH_KEY, data.message || done);
    } catch (error) {
      // 잠글 때 body 를 inert 로 만들었으므로 풀고 나서 포커스를 돌려준다 (잠긴 동안 포커스가 밖으로 나간다)
      unlock();
      setNotice(status, error.message);
      button.focus();
    }
  });
};

export default InitPartnerProfile;
